// src/userPanel/components/FotoIDModal.js
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { toast } from 'react-toastify';
import { obtenerFotoIDStatus, subirFotoID, procesarFotoID, guardarAnalisisFotoID } from '../../services/api/modules/fotoID';

/**
 * Resize and compress an image file using canvas.
 * Returns a JPEG File capped at maxWidth x maxHeight, quality 0.85.
 */
function processImage(file, maxWidth = 800, maxHeight = 800) {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        let { width, height } = img;

        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        if (height > maxHeight) {
          width = (width * maxHeight) / height;
          height = maxHeight;
        }

        const canvas = document.createElement('canvas');
        canvas.width = Math.round(width);
        canvas.height = Math.round(height);

        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

        canvas.toBlob(
          (blob) => {
            const name = file.name.replace(/\.[^.]+$/, '') + '.jpg';
            resolve(new File([blob], name, { type: 'image/jpeg' }));
          },
          'image/jpeg',
          0.85
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

/**
 * Apply a crop defined by {x, y, width, height} (all in 0-1 fractions)
 * to an image loaded from `src` (object URL or data URL).
 * Returns a File (JPEG).
 */
function applyCrop(src, crop, outputName) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const sx = Math.round(crop.x * img.naturalWidth);
      const sy = Math.round(crop.y * img.naturalHeight);
      const sw = Math.round(crop.width * img.naturalWidth);
      const sh = Math.round(crop.height * img.naturalHeight);

      const canvas = document.createElement('canvas');
      // Cap output to 800px on longest side
      let outW = sw;
      let outH = sh;
      const max = 800;
      if (outW > max || outH > max) {
        if (outW >= outH) {
          outH = Math.round((outH * max) / outW);
          outW = max;
        } else {
          outW = Math.round((outW * max) / outH);
          outH = max;
        }
      }
      canvas.width = outW;
      canvas.height = outH;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);

      canvas.toBlob(
        (blob) => resolve(new File([blob], outputName, { type: 'image/jpeg' })),
        'image/jpeg',
        0.85
      );
    };
    img.src = src;
  });
}

/**
 * Parse a Firestore timestamp (could be Timestamp object, {_seconds}, ISO string, or ms).
 * Returns a formatted date string or 'Recién guardado' if unparseable.
 */
function formatFirestoreDate(val) {
  if (!val) return 'Recién guardado';
  try {
    let date;
    if (val.toDate) {
      date = val.toDate();
    } else if (val._seconds) {
      date = new Date(val._seconds * 1000);
    } else if (val.seconds) {
      date = new Date(val.seconds * 1000);
    } else {
      date = new Date(val);
    }
    if (isNaN(date.getTime())) return 'Recién guardado';
    return date.toLocaleDateString('es-MX');
  } catch {
    return 'Recién guardado';
  }
}

/**
 * Convert old proxy URLs (/api/archivo/foto-id/...) to public Storage URLs.
 * Photos uploaded after the makePublic fix already have the correct URL.
 */
function getPublicPhotoUrl(url) {
  if (!url) return null;
  if (url.includes('storage.googleapis.com')) return url;
  const match = url.match(/\/api\/archivo\/foto-id\/([^/]+)\/(.+)/);
  if (match) {
    const [, objetoId, filename] = match;
    const bucket = process.env.REACT_APP_ENV === 'testing'
      ? process.env.REACT_APP_STORAGE_BUCKET_TEST
      : process.env.REACT_APP_STORAGE_BUCKET_PROD;
    return `https://storage.googleapis.com/${bucket}/foto-id/${objetoId}/${filename}`;
  }
  return url;
}

// ─── Species-specific options ─────────────────────────────

const COLORES_PELAJE = {
  comunes: ['Blanco', 'Negro', 'Café', 'Dorado', 'Crema', 'Gris', 'Rojo', 'Chocolate'],
  perro: ['Sal y Pimienta', 'Tricolor', 'Bicolor', 'Merle', 'Brindle'],
  gato: ['Tabby', 'Calicó', 'Carey', 'Atigrado', 'Point (siamés)', 'Ahumado']
};

const COLORES_OJOS = {
  comunes: ['Café', 'Negro', 'Miel', 'Verde'],
  perro: ['Ámbar', 'Azul', 'Bicolor'],
  gato: ['Amarillo', 'Azul', 'Cobre', 'Bicolor', 'Odd-eye']
};

const TIPOS_PELAJE = {
  perro: ['Corto', 'Largo', 'Doble capa', 'Alambre', 'Rizado', 'Liso', 'Sin pelo'],
  gato: ['Corto', 'Largo', 'Semi-largo', 'Rizado (Rex)', 'Sin pelo (Sphynx)']
};

function getOpcionesEspecie(tipo, especie) {
  const esp = (especie || '').toLowerCase();
  if (tipo === 'tiposPelaje') {
    return TIPOS_PELAJE[esp] || TIPOS_PELAJE.perro;
  }
  const data = tipo === 'coloresPelaje' ? COLORES_PELAJE : COLORES_OJOS;
  return [...(data.comunes || []), ...(data[esp] || [])];
}

// ─── Main Modal ───────────────────────────────────────────

export default function FotoIDModal({ mascota, onClose, onFotoIDUpdated }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [fotoID, setFotoID] = useState(null);
  // 'loading' | 'intro' | 'capture' | 'crop' | 'dashboard'
  const [mode, setMode] = useState('loading');

  // Detección de móvil para fullscreen
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Collapsible photos in dashboard
  const [fotosExpandidas, setFotosExpandidas] = useState(false);

  // Files ready for upload (already processed)
  const [frontalFile, setFrontalFile] = useState(null);
  const [lateralFile, setLateralFile] = useState(null);
  const [frontalPreview, setFrontalPreview] = useState(null);
  const [lateralPreview, setLateralPreview] = useState(null);

  // Crop state – when non-null we show the crop overlay
  const [cropState, setCropState] = useState(null);
  // { tipo: 'frontal'|'lateral', src: objectURL, originalFile: File }

  // Edit mode state
  const [editando, setEditando] = useState(false);
  const [savingEdit, setSavingEdit] = useState(false);
  const [editData, setEditData] = useState(null);

  // No refs needed – we use <label htmlFor> to trigger native file pickers

  useEffect(() => { loadFotoID(); }, [mascota.id]);

  useEffect(() => {
    return () => {
      if (frontalPreview) URL.revokeObjectURL(frontalPreview);
      if (lateralPreview) URL.revokeObjectURL(lateralPreview);
    };
  }, [frontalPreview, lateralPreview]);

  async function loadFotoID() {
    setLoading(true);
    const result = await obtenerFotoIDStatus(mascota.id);
    setLoading(false);
    if (result.error) { setMode('intro'); return; }
    if (result.completo && result.fotos) { setFotoID(result); setMode('dashboard'); }
    else { setMode('intro'); }
  }

  function handleFileSelect(tipo, file) {
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('Solo se permiten archivos de imagen'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('La imagen no debe superar 10MB'); return; }
    // Open crop overlay
    const src = URL.createObjectURL(file);
    setCropState({ tipo, src, originalFile: file });
  }

  async function handleCropConfirm(croppedFile, preview) {
    if (!cropState) return;
    const { tipo, src } = cropState;
    URL.revokeObjectURL(src);

    if (tipo === 'frontal') {
      if (frontalPreview) URL.revokeObjectURL(frontalPreview);
      setFrontalFile(croppedFile);
      setFrontalPreview(preview);
    } else {
      if (lateralPreview) URL.revokeObjectURL(lateralPreview);
      setLateralFile(croppedFile);
      setLateralPreview(preview);
    }
    setCropState(null);
  }

  function handleCropCancel() {
    if (cropState) URL.revokeObjectURL(cropState.src);
    setCropState(null);
  }

  async function handleSave() {
    if (!frontalFile && !lateralFile) { toast.error('Selecciona al menos una foto'); return; }
    setSaving(true); setProgress(0);

    if (frontalFile) {
      const r1 = await subirFotoID(mascota.id, frontalFile, 'frontal', (p) => {
        setProgress(lateralFile ? Math.round(p / 2) : p);
      });
      if (r1.error) { toast.error(`Error subiendo frontal: ${r1.error}`); setSaving(false); return; }
    }
    if (lateralFile) {
      const r2 = await subirFotoID(mascota.id, lateralFile, 'lateral', (p) => {
        setProgress(frontalFile ? 50 + Math.round(p / 2) : p);
      });
      if (r2.error) { toast.error(`Error subiendo lateral: ${r2.error}`); setSaving(false); return; }
    }

    setSaving(false); setProgress(100);
    setProcessing(true);
    const processResult = await procesarFotoID(mascota.id);
    setProcessing(false);

    if (processResult.error) {
      toast.info('Fotos guardadas. El procesamiento con IA se completará pronto.');
    } else {
      toast.success('Foto-ID guardada y procesada correctamente');
    }
    if (onFotoIDUpdated) onFotoIDUpdated({ completo: true });

    // Reload and show dashboard
    const updated = await obtenerFotoIDStatus(mascota.id);
    if (!updated.error && updated.completo) {
      setFotoID(updated);
      setMode('dashboard');
    } else {
      onClose();
    }
  }

  function handleSwitchToCapture() {
    setMode('capture');
    setEditando(false);
    setFrontalFile(null); setLateralFile(null);
    setFrontalPreview(null); setLateralPreview(null);
  }

  function handleStartEdit() {
    const analisis = fotoID?.analisis || {};
    const colores = analisis.coloresPelaje || [];
    setEditData({
      colorPrincipal: analisis.colorPrincipal || colores[0] || '',
      coloresSecundarios: analisis.coloresSecundarios || colores.slice(1) || [],
      colorOjos: analisis.colorOjos || '',
      tipoPelaje: analisis.tipoPelaje || '',
      manchasDetalladas: (analisis.manchasDetalladas || analisis.senasParticulares || []).join('\n')
    });
    setEditando(true);
  }

  function handleCancelEdit() {
    setEditando(false);
    setEditData(null);
  }

  async function handleSaveEdit() {
    if (!editData) return;
    setSavingEdit(true);

    const payload = {
      colorPrincipal: editData.colorPrincipal,
      coloresSecundarios: editData.coloresSecundarios,
      colorOjos: editData.colorOjos,
      tipoPelaje: editData.tipoPelaje,
      manchasDetalladas: editData.manchasDetalladas
        .split('\n')
        .map(s => s.trim())
        .filter(Boolean)
    };

    const result = await guardarAnalisisFotoID(mascota.id, payload);
    setSavingEdit(false);

    if (result.error) {
      toast.error(`Error al guardar: ${result.error}`);
      return;
    }

    toast.success('Datos actualizados correctamente');
    setEditando(false);
    setEditData(null);

    // Reload to get fresh data
    const updated = await obtenerFotoIDStatus(mascota.id);
    if (!updated.error) {
      setFotoID(updated);
    }
  }

  function toggleSecundario(color) {
    setEditData(prev => {
      const arr = prev.coloresSecundarios || [];
      if (arr.includes(color)) {
        return { ...prev, coloresSecundarios: arr.filter(c => c !== color) };
      }
      return { ...prev, coloresSecundarios: [...arr, color] };
    });
  }

  // ─── Crop overlay ────────────────────────────────
  if (cropState) {
    return createPortal(
      <div style={getOverlayStyle(isMobile)}>
        <ImageCropper
          src={cropState.src}
          tipo={cropState.tipo}
          originalFile={cropState.originalFile}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
          isMobile={isMobile}
        />
      </div>,
      document.body
    );
  }

  // ─── Loading ─────────────────────────────────────
  if (loading || mode === 'loading') {
    return createPortal(
      <div style={getOverlayStyle(isMobile)}>
        <div style={getModalContainerStyle(isMobile)}>
          <div className="text-center" style={{ padding: 'var(--space-6)' }}>
            <div className="spinner-border" style={{ color: 'var(--feature-primary)' }} role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3 text-muted">Cargando Foto-ID...</p>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // ─── Intro ───────────────────────────────────────
  if (mode === 'intro') {
    return createPortal(
      <div style={getOverlayStyle(isMobile)}>
        <div style={getModalContainerStyle(isMobile)}>
          <div style={getHeaderStyle(isMobile)}>
            <h5 style={{ margin: 0, color: 'var(--white)', fontWeight: 'var(--font-weight-bold)' }}>
              <i className="bi bi-camera me-2"></i>Foto-ID de {mascota.nombre || 'Mascota'}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose} aria-label="Cerrar"></button>
          </div>
          <div style={{ padding: 'var(--space-4)', overflowY: 'auto', flex: 1 }}>
            <h6 style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-lg)', marginBottom: '16px' }}>
              ¿Para qué sirve?
            </h6>
            <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)', marginBottom: '16px', lineHeight: '1.6' }}>
              Con 2 fotos desde distintos ángulos, nuestra IA genera automáticamente:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '20px' }}>
              {[
                ['🎨', 'Colores exactos de pelaje'],
                ['👁️', 'Color de ojos'],
                ['📝', 'Señas particulares únicas'],
                ['🐕', 'Tipo de pelaje'],
              ].map(([icon, text]) => (
                <div key={text} style={featureRowStyle}>
                  <span style={{ fontSize: '1.2rem' }}>{icon}</span>
                  <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-800)' }}>{text}</span>
                </div>
              ))}
            </div>
            <h6 style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-base)', marginBottom: '12px' }}>
              ¿Por qué importa?
            </h6>
            <ul style={{ paddingLeft: '20px', marginBottom: '20px', listStyle: 'disc' }}>
              <li style={liStyle}>Si se pierde, tienes una descripción completa para compartir en redes sociales</li>
              <li style={liStyle}>Genera un carnet de identificación profesional</li>
              <li style={{ ...liStyle, marginBottom: 0 }}>Compatible con registros oficiales de mascotas</li>
            </ul>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '10px 14px', backgroundColor: 'var(--gray-100)', borderRadius: 'var(--radius-md)', marginBottom: '8px' }}>
              <span style={{ fontSize: '1.1rem' }}>⏱️</span>
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)' }}>Toma menos de 2 minutos</span>
            </div>
          </div>
          <div style={{ ...footerStyle, justifyContent: 'center' }}>
            <button className="btn btn-feature transition-base" onClick={() => setMode('capture')} style={{ padding: '10px 32px' }}>
              Comenzar
            </button>
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // ─── Dashboard ───────────────────────────────────
  if (mode === 'dashboard') {
    const analisis = fotoID?.analisis || {};
    const especie = (mascota.especie || 'perro').toLowerCase();

    // Handle both formats: coloresPelaje array or colorPrincipal + coloresSecundarios
    const colores = analisis.coloresPelaje || [];
    const colorPrincipal = analisis.colorPrincipal || colores[0] || null;
    const coloresSecundarios = analisis.coloresSecundarios || colores.slice(1);
    const patron = analisis.patron || null;

    const colorOjos = analisis.colorOjos || null;
    const tipoPelaje = analisis.tipoPelaje || null;
    const manchas = analisis.manchasDetalladas || analisis.senasParticulares || [];
    const anomalias = analisis.anomaliasAnatomicas || [];
    const complexion = analisis.complexion || null;
    const descripcion = analisis.descripcionParaCartel || analisis.descripcionGeneral || null;
    const raza = analisis.raza || null;
    const razaNombre = raza?.sugerida || raza?.razaSugerida || null;

    // Options filtered by species (for edit mode)
    const opcionesPelaje = getOpcionesEspecie('coloresPelaje', especie);
    const opcionesOjos = getOpcionesEspecie('coloresOjos', especie);
    const opcionesTipoPelaje = getOpcionesEspecie('tiposPelaje', especie);

    return createPortal(
      <div style={getOverlayStyle(isMobile)}>
        <div style={getModalContainerStyle(isMobile)}>
          <div style={getHeaderStyle(isMobile)}>
            <h5 style={{ margin: 0, color: 'var(--white)', fontWeight: 'var(--font-weight-bold)' }}>
              <i className="bi bi-camera me-2"></i>Foto-ID de {mascota.nombre || 'Mascota'}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose} aria-label="Cerrar"></button>
          </div>
          <div style={{ padding: 'var(--space-4)', overflowY: 'auto', flex: 1 }}>
            {/* Photos – collapsible */}
            {(() => {
              const tieneFotos = fotoID?.fotos?.frontal || fotoID?.fotos?.lateral;
              const numFotos = (fotoID?.fotos?.frontal ? 1 : 0) + (fotoID?.fotos?.lateral ? 1 : 0);
              return (
                <div style={{ marginBottom: '16px' }}>
                  {/* Collapsible header */}
                  <button
                    type="button"
                    onClick={() => setFotosExpandidas(!fotosExpandidas)}
                    style={{
                      width: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 12px',
                      border: '1px solid var(--gray-200)',
                      borderRadius: 'var(--radius-md)',
                      backgroundColor: 'var(--gray-50)',
                      cursor: 'pointer',
                      transition: 'var(--transition-fast)'
                    }}
                  >
                    <i className="bi bi-camera" style={{ fontSize: '1rem', color: 'var(--gray-600)' }}></i>
                    <span style={{ fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)', color: 'var(--gray-800)' }}>
                      Fotos {tieneFotos ? `(${numFotos})` : ''}
                    </span>
                    {/* Thumbnails when collapsed and has photos */}
                    {!fotosExpandidas && tieneFotos && (
                      <div style={{ display: 'flex', gap: '6px', marginLeft: '8px' }}>
                        {fotoID?.fotos?.frontal && (
                          <img
                            src={getPublicPhotoUrl(fotoID.fotos.frontal)}
                            alt="Frontal"
                            style={{ width: '36px', height: '36px', borderRadius: 'var(--radius-sm, 4px)', objectFit: 'cover', border: '1px solid var(--gray-200)' }}
                          />
                        )}
                        {fotoID?.fotos?.lateral && (
                          <img
                            src={getPublicPhotoUrl(fotoID.fotos.lateral)}
                            alt="Lateral"
                            style={{ width: '36px', height: '36px', borderRadius: 'var(--radius-sm, 4px)', objectFit: 'cover', border: '1px solid var(--gray-200)' }}
                          />
                        )}
                      </div>
                    )}
                    <i
                      className={`bi bi-chevron-${fotosExpandidas ? 'down' : 'right'}`}
                      style={{ marginLeft: 'auto', fontSize: 'var(--font-size-sm)', color: 'var(--gray-500)' }}
                    ></i>
                  </button>

                  {/* Expanded content */}
                  {fotosExpandidas && (
                    <div style={{ marginTop: '12px' }}>
                      <div className="row g-3">
                        <div className="col-6">
                          <div className="text-center">
                            <span className="d-block mb-2 fw-semibold" style={{ fontSize: 'var(--font-size-sm)' }}>
                              <i className="bi bi-person-bounding-box me-1"></i>Frontal
                            </span>
                            <FotoIDImage src={getPublicPhotoUrl(fotoID?.fotos?.frontal)} alt="Foto frontal" label="Frontal" />
                          </div>
                        </div>
                        <div className="col-6">
                          <div className="text-center">
                            <span className="d-block mb-2 fw-semibold" style={{ fontSize: 'var(--font-size-sm)' }}>
                              <i className="bi bi-arrow-left-right me-1"></i>Lateral
                            </span>
                            <FotoIDImage src={getPublicPhotoUrl(fotoID?.fotos?.lateral)} alt="Foto lateral" label="Lateral" />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })()}

            <hr style={{ borderColor: 'var(--gray-200)', margin: '0 0 16px 0' }} />

            {/* ─── EDIT MODE ─── */}
            {editando && editData ? (
              <>
                {/* Color principal */}
                <div style={dashboardFieldStyle}>
                  <div style={dashboardLabelStyle}>
                    <span style={{ fontSize: '1.1rem' }}>🎨</span>
                    <span>Color principal</span>
                  </div>
                  <select
                    value={editData.colorPrincipal}
                    onChange={(e) => setEditData({ ...editData, colorPrincipal: e.target.value })}
                    style={editSelectStyle}
                  >
                    <option value="">Seleccionar...</option>
                    {opcionesPelaje.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                {/* Colores secundarios */}
                <div style={dashboardFieldStyle}>
                  <div style={dashboardLabelStyle}>
                    <span style={{ fontSize: '1.1rem' }}>🎨</span>
                    <span>Colores secundarios</span>
                  </div>
                  <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                    {opcionesPelaje
                      .filter(c => c !== editData.colorPrincipal)
                      .map(color => {
                        const selected = (editData.coloresSecundarios || []).includes(color);
                        return (
                          <button
                            key={color}
                            type="button"
                            onClick={() => toggleSecundario(color)}
                            style={{
                              padding: '4px 12px',
                              borderRadius: 'var(--radius-full)',
                              border: selected ? '2px solid var(--feature-primary)' : '1px solid var(--gray-300)',
                              backgroundColor: selected ? 'var(--feature-primary)' : 'var(--white)',
                              color: selected ? 'var(--white)' : 'var(--gray-700)',
                              fontSize: 'var(--font-size-xs)',
                              cursor: 'pointer',
                              transition: 'var(--transition-fast)'
                            }}
                          >
                            {color}
                          </button>
                        );
                      })}
                  </div>
                </div>

                {/* Color de ojos */}
                <div style={dashboardFieldStyle}>
                  <div style={dashboardLabelStyle}>
                    <span style={{ fontSize: '1.1rem' }}>👁️</span>
                    <span>Color de ojos</span>
                  </div>
                  <select
                    value={editData.colorOjos}
                    onChange={(e) => setEditData({ ...editData, colorOjos: e.target.value })}
                    style={editSelectStyle}
                  >
                    <option value="">Seleccionar...</option>
                    {opcionesOjos.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                {/* Tipo de pelaje */}
                <div style={dashboardFieldStyle}>
                  <div style={dashboardLabelStyle}>
                    <span style={{ fontSize: '1.1rem' }}>🐕</span>
                    <span>Tipo de pelaje</span>
                  </div>
                  <select
                    value={editData.tipoPelaje}
                    onChange={(e) => setEditData({ ...editData, tipoPelaje: e.target.value })}
                    style={editSelectStyle}
                  >
                    <option value="">Seleccionar...</option>
                    {opcionesTipoPelaje.map(c => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                </div>

                {/* Manchas y marcas */}
                <div style={dashboardFieldStyle}>
                  <div style={dashboardLabelStyle}>
                    <span style={{ fontSize: '1.1rem' }}>🔍</span>
                    <span>Manchas y marcas</span>
                  </div>
                  <textarea
                    value={editData.manchasDetalladas}
                    onChange={(e) => setEditData({ ...editData, manchasDetalladas: e.target.value })}
                    placeholder="Una marca por línea..."
                    rows={4}
                    style={editTextareaStyle}
                  />
                  <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-400)', margin: '4px 0 0 0' }}>
                    Escribe una marca por línea
                  </p>
                </div>
              </>
            ) : (
              /* ─── VIEW MODE ─── */
              <>
                {/* Colores */}
                {colorPrincipal && (
                  <div style={dashboardFieldStyle}>
                    <div style={dashboardLabelStyle}>
                      <span style={{ fontSize: '1.1rem' }}>🎨</span>
                      <span>Colores</span>
                    </div>
                    <div style={dashboardValueStyle}>
                      {colorPrincipal}
                      {patron && (
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', marginLeft: '8px' }}>
                          | Patrón: {patron}
                        </span>
                      )}
                    </div>
                    {coloresSecundarios.length > 0 && (
                      <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', margin: '6px 0 0 0' }}>
                        Secundarios: {coloresSecundarios.join(', ')}
                      </p>
                    )}
                  </div>
                )}

                {/* Complexión */}
                {complexion && (
                  <div style={dashboardFieldStyle}>
                    <div style={dashboardLabelStyle}>
                      <span style={{ fontSize: '1.1rem' }}>📏</span>
                      <span>Complexión</span>
                    </div>
                    <div style={dashboardValueStyle}>
                      {[complexion.tamano, complexion.contextura, complexion.formaCara ? `cara ${complexion.formaCara}` : null]
                        .filter(Boolean)
                        .join(' \u2022 ')}
                    </div>
                  </div>
                )}

                {/* Manchas y marcas */}
                {manchas.length > 0 && (
                  <div style={dashboardFieldStyle}>
                    <div style={dashboardLabelStyle}>
                      <span style={{ fontSize: '1.1rem' }}>🔍</span>
                      <span>Manchas y marcas</span>
                    </div>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', listStyle: 'disc' }}>
                      {manchas.map((s, i) => (
                        <li key={i} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)', marginBottom: '4px', lineHeight: '1.5' }}>
                          {s}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Anomalías anatómicas */}
                {anomalias.length > 0 && (
                  <div style={dashboardFieldStyle}>
                    <div style={{ ...dashboardLabelStyle, color: 'var(--warning-dark, #b45309)' }}>
                      <span style={{ fontSize: '1.1rem' }}>⚠️</span>
                      <span>Anomalías distintivas</span>
                    </div>
                    <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', listStyle: 'disc' }}>
                      {anomalias.map((a, i) => (
                        <li key={i} style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)', marginBottom: '4px', lineHeight: '1.5' }}>
                          {a}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Color de ojos */}
                {colorOjos && (
                  <div style={dashboardFieldStyle}>
                    <div style={dashboardLabelStyle}>
                      <span style={{ fontSize: '1.1rem' }}>👁️</span>
                      <span>Color de ojos</span>
                    </div>
                    <div style={dashboardValueStyle}>{colorOjos}</div>
                  </div>
                )}

                {/* Tipo de pelaje */}
                {tipoPelaje && (
                  <div style={dashboardFieldStyle}>
                    <div style={dashboardLabelStyle}>
                      <span style={{ fontSize: '1.1rem' }}>🐕</span>
                      <span>Tipo de pelaje</span>
                    </div>
                    <div style={dashboardValueStyle}>{tipoPelaje}</div>
                  </div>
                )}

                {/* Raza detectada */}
                {razaNombre && (
                  <div style={dashboardFieldStyle}>
                    <div style={dashboardLabelStyle}>
                      <span style={{ fontSize: '1.1rem' }}>🏷️</span>
                      <span>Raza detectada</span>
                    </div>
                    <div style={dashboardValueStyle}>
                      {razaNombre}
                      {raza.confianza && (
                        <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', marginLeft: '8px' }}>
                          ({raza.confianza}% confianza)
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Descripción para cartel */}
                {descripcion && (
                  <div style={dashboardFieldStyle}>
                    <div style={dashboardLabelStyle}>
                      <span style={{ fontSize: '1.1rem' }}>📋</span>
                      <span>Descripción para cartel</span>
                    </div>
                    <p style={{ margin: '8px 0 0 0', fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)', lineHeight: '1.6', fontStyle: 'italic' }}>
                      &ldquo;{descripcion}&rdquo;
                    </p>
                  </div>
                )}
              </>
            )}

            <hr style={{ borderColor: 'var(--gray-200)', margin: '16px 0' }} />

            {fotoID?.fechaCreacion && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', justifyContent: 'center', marginBottom: '4px' }}>
                <span style={{ fontSize: '1rem' }}>⏱️</span>
                <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)' }}>
                  Analizado el {formatFirestoreDate(fotoID.fechaCreacion)}
                </span>
              </div>
            )}
          </div>
          <div style={footerStyle}>
            {editando ? (
              <>
                <button className="btn btn-outline-secondary btn-sm transition-base" onClick={handleCancelEdit} disabled={savingEdit}>
                  <i className="bi bi-x-lg me-1"></i>Cancelar
                </button>
                <button className="btn btn-feature btn-sm transition-base" onClick={handleSaveEdit} disabled={savingEdit}>
                  {savingEdit
                    ? <><span className="spinner-border spinner-border-sm me-1" role="status"></span>Guardando...</>
                    : <><i className="bi bi-check-lg me-1"></i>Guardar cambios</>}
                </button>
              </>
            ) : (
              <>
                <button className="btn btn-outline-secondary btn-sm transition-base" onClick={onClose}>
                  <i className="bi bi-x-lg me-1"></i>Cerrar
                </button>
                <button className="btn btn-outline-primary btn-sm transition-base" onClick={handleStartEdit}>
                  <i className="bi bi-pencil me-1"></i>Editar datos
                </button>
                <button className="btn btn-feature btn-sm transition-base" onClick={handleSwitchToCapture}>
                  <i className="bi bi-arrow-repeat me-1"></i>Actualizar fotos
                </button>
              </>
            )}
          </div>
        </div>
      </div>,
      document.body
    );
  }

  // ─── Capture ─────────────────────────────────────
  return createPortal(
    <div style={getOverlayStyle(isMobile)}>
      <div style={getModalContainerStyle(isMobile)}>
        <div style={getHeaderStyle(isMobile)}>
          <h5 style={{ margin: 0, color: 'var(--white)', fontWeight: 'var(--font-weight-bold)' }}>
            <i className="bi bi-camera me-2"></i>Foto-ID de {mascota.nombre || 'Mascota'}
          </h5>
          <button type="button" className="btn-close btn-close-white" onClick={onClose} aria-label="Cerrar"></button>
        </div>
        <div style={{ padding: 'var(--space-4)', overflowY: 'auto', flex: 1 }}>
          <div className="alert alert-info py-2 px-3 mb-3" style={{ fontSize: 'var(--font-size-sm)', borderRadius: 'var(--radius-md)' }}>
            <i className="bi bi-info-circle me-1"></i>
            Sube una foto frontal y lateral de tu mascota para crear su Foto-ID.
          </div>

          <div className="row g-3">
            {/* Frontal */}
            <div className="col-6">
              <div className="text-center">
                <span className="d-block mb-2 fw-semibold" style={{ fontSize: 'var(--font-size-sm)' }}>
                  <i className="bi bi-person-bounding-box me-1"></i>Frontal
                </span>
                <label htmlFor="fotoid-frontal-upload" style={{ ...uploadAreaStyle(!!frontalPreview), display: 'block', margin: 0 }}>
                  {frontalPreview
                    ? <img src={frontalPreview} alt="Vista previa frontal" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'var(--radius-md)' }} />
                    : <div style={placeholderStyle}><i className="bi bi-camera" style={{ fontSize: '2rem', color: 'var(--gray-400)' }}></i><span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', marginTop: '4px' }}>Toca para subir</span></div>
                  }
                </label>
                <input id="fotoid-frontal-upload" type="file" accept="image/*" disabled={saving || processing} style={{ position: 'absolute', width: 0, height: 0, opacity: 0, overflow: 'hidden', pointerEvents: 'none' }} onChange={(e) => { handleFileSelect('frontal', e.target.files[0]); e.target.value = ''; }} />
              </div>
            </div>
            {/* Lateral */}
            <div className="col-6">
              <div className="text-center">
                <span className="d-block mb-2 fw-semibold" style={{ fontSize: 'var(--font-size-sm)' }}>
                  <i className="bi bi-arrow-left-right me-1"></i>Lateral
                </span>
                <label htmlFor="fotoid-lateral-upload" style={{ ...uploadAreaStyle(!!lateralPreview), display: 'block', margin: 0 }}>
                  {lateralPreview
                    ? <img src={lateralPreview} alt="Vista previa lateral" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'var(--radius-md)' }} />
                    : <div style={placeholderStyle}><i className="bi bi-camera" style={{ fontSize: '2rem', color: 'var(--gray-400)' }}></i><span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', marginTop: '4px' }}>Toca para subir</span></div>
                  }
                </label>
                <input id="fotoid-lateral-upload" type="file" accept="image/*" disabled={saving || processing} style={{ position: 'absolute', width: 0, height: 0, opacity: 0, overflow: 'hidden', pointerEvents: 'none' }} onChange={(e) => { handleFileSelect('lateral', e.target.files[0]); e.target.value = ''; }} />
              </div>
            </div>
          </div>

          {saving && (
            <div className="mt-3">
              <div className="progress" style={{ height: '8px', borderRadius: 'var(--radius-full)' }}>
                <div className="progress-bar" role="progressbar" style={{ width: `${progress}%`, background: 'linear-gradient(135deg, var(--feature-primary), var(--feature-secondary))', transition: 'width 0.3s ease' }} aria-valuenow={progress} aria-valuemin="0" aria-valuemax="100"></div>
              </div>
              <p className="text-muted text-center mt-1" style={{ fontSize: 'var(--font-size-xs)' }}>Subiendo fotos... {progress}%</p>
            </div>
          )}
          {processing && (
            <div className="mt-3 text-center">
              <div className="spinner-border spinner-border-sm me-2" style={{ color: 'var(--feature-primary)' }} role="status"></div>
              <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--gray-600)' }}>Procesando con IA...</span>
            </div>
          )}
        </div>
        <div style={footerStyle}>
          <button className="btn btn-outline-secondary btn-sm transition-base" onClick={onClose} disabled={saving || processing}>
            <i className="bi bi-x-lg me-1"></i>Cancelar
          </button>
          <button className="btn btn-feature btn-sm transition-base" onClick={handleSave} disabled={saving || processing || (!frontalFile && !lateralFile)}>
            {saving || processing
              ? <><span className="spinner-border spinner-border-sm me-1" role="status"></span>{processing ? 'Procesando...' : 'Guardando...'}</>
              : <><i className="bi bi-check-lg me-1"></i>Guardar Foto-ID</>}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

// ─── ImageCropper ──────────────────────────────────────────
// Provides zoom + drag inside a fixed viewport, then exports the visible region.

function ImageCropper({ src, tipo, originalFile, onConfirm, onCancel, isMobile = false }) {
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imgSize, setImgSize] = useState(null); // { naturalWidth, naturalHeight }
  const containerRef = useRef(null);

  // Aspect ratio: frontal = 1:1 (square face), lateral = 4:3 (body)
  const aspectRatio = tipo === 'frontal' ? 1 : 4 / 3;

  const handleImageLoad = useCallback((e) => {
    const { naturalWidth, naturalHeight } = e.target;
    setImgSize({ naturalWidth, naturalHeight });
    // Compute initial zoom so the image covers the viewport
    const viewportW = containerRef.current?.clientWidth || 300;
    const viewportH = viewportW / aspectRatio;
    const scaleW = viewportW / naturalWidth;
    const scaleH = viewportH / naturalHeight;
    const initialZoom = Math.max(scaleW, scaleH);
    setZoom(initialZoom);
    // Center
    const renderedW = naturalWidth * initialZoom;
    const renderedH = naturalHeight * initialZoom;
    setOffset({
      x: (viewportW - renderedW) / 2,
      y: (viewportH - renderedH) / 2
    });
  }, [aspectRatio]);

  // Pointer handlers (touch + mouse)
  const handlePointerDown = (e) => {
    e.preventDefault();
    setDragging(true);
    setDragStart({ x: e.clientX - offset.x, y: e.clientY - offset.y });
  };
  const handlePointerMove = (e) => {
    if (!dragging) return;
    setOffset({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handlePointerUp = () => setDragging(false);

  // Zoom via slider
  const minZoom = (() => {
    if (!imgSize || !containerRef.current) return 0.1;
    const viewportW = containerRef.current.clientWidth;
    const viewportH = viewportW / aspectRatio;
    return Math.max(viewportW / imgSize.naturalWidth, viewportH / imgSize.naturalHeight);
  })();

  async function handleConfirm() {
    if (!imgSize || !containerRef.current) return;
    const viewportW = containerRef.current.clientWidth;
    const viewportH = viewportW / aspectRatio;

    // Map viewport rect back to image coordinates
    const cropX = -offset.x / (imgSize.naturalWidth * zoom);
    const cropY = -offset.y / (imgSize.naturalHeight * zoom);
    const cropW = viewportW / (imgSize.naturalWidth * zoom);
    const cropH = viewportH / (imgSize.naturalHeight * zoom);

    // Clamp
    const crop = {
      x: Math.max(0, Math.min(1 - cropW, cropX)),
      y: Math.max(0, Math.min(1 - cropH, cropY)),
      width: Math.min(1, cropW),
      height: Math.min(1, cropH)
    };

    const name = originalFile.name.replace(/\.[^.]+$/, '') + '_crop.jpg';
    const croppedFile = await applyCrop(src, crop, name);
    const preview = URL.createObjectURL(croppedFile);
    onConfirm(croppedFile, preview);
  }

  const viewportW = containerRef.current?.clientWidth || 300;
  const viewportH = viewportW / aspectRatio;

  return (
    <div style={getModalContainerStyle(isMobile)}>
      <div style={getHeaderStyle(isMobile)}>
        <h5 style={{ margin: 0, color: 'var(--white)', fontWeight: 'var(--font-weight-bold)' }}>
          <i className="bi bi-crop me-2"></i>
          Ajustar foto {tipo === 'frontal' ? 'frontal' : 'lateral'}
        </h5>
        <button type="button" className="btn-close btn-close-white" onClick={onCancel} aria-label="Cancelar"></button>
      </div>

      <div style={{ padding: 'var(--space-3)', flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', overflow: 'hidden' }}>
        <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--gray-500)', marginBottom: '8px', textAlign: 'center' }}>
          Arrastra para mover • Usa el slider para hacer zoom
        </p>

        {/* Viewport */}
        <div
          ref={containerRef}
          style={{
            width: '100%',
            maxWidth: '360px',
            height: viewportH || 270,
            overflow: 'hidden',
            borderRadius: 'var(--radius-md)',
            border: '2px solid var(--gray-300)',
            position: 'relative',
            cursor: dragging ? 'grabbing' : 'grab',
            touchAction: 'none',
            backgroundColor: 'var(--gray-900)'
          }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerUp}
        >
          <img
            src={src}
            alt="Crop preview"
            draggable={false}
            onLoad={handleImageLoad}
            style={{
              position: 'absolute',
              left: offset.x,
              top: offset.y,
              width: imgSize ? imgSize.naturalWidth * zoom : 'auto',
              height: imgSize ? imgSize.naturalHeight * zoom : 'auto',
              maxWidth: 'none',
              pointerEvents: 'none',
              userSelect: 'none'
            }}
          />
        </div>

        {/* Zoom slider */}
        <div style={{ width: '100%', maxWidth: '360px', marginTop: '12px', display: 'flex', alignItems: 'center', gap: '8px' }}>
          <i className="bi bi-dash" style={{ color: 'var(--gray-500)' }}></i>
          <input
            type="range"
            min={minZoom}
            max={Math.max(minZoom * 3, 1)}
            step={0.001}
            value={zoom}
            onChange={(e) => {
              const newZoom = parseFloat(e.target.value);
              if (!imgSize || !containerRef.current) { setZoom(newZoom); return; }
              // Zoom towards center of viewport
              const vpW = containerRef.current.clientWidth;
              const vpH = vpW / aspectRatio;
              const cx = vpW / 2;
              const cy = vpH / 2;
              const imgXAtCenter = (cx - offset.x) / zoom;
              const imgYAtCenter = (cy - offset.y) / zoom;
              setOffset({
                x: cx - imgXAtCenter * newZoom,
                y: cy - imgYAtCenter * newZoom
              });
              setZoom(newZoom);
            }}
            style={{ flex: 1, accentColor: 'var(--feature-primary)' }}
          />
          <i className="bi bi-plus" style={{ color: 'var(--gray-500)' }}></i>
        </div>
      </div>

      <div style={footerStyle}>
        <button className="btn btn-outline-secondary btn-sm transition-base" onClick={onCancel}>
          <i className="bi bi-x-lg me-1"></i>Cancelar
        </button>
        <button className="btn btn-feature btn-sm transition-base" onClick={handleConfirm}>
          <i className="bi bi-check-lg me-1"></i>Confirmar
        </button>
      </div>
    </div>
  );
}

// ─── FotoIDImage ───────────────────────────────────────────

function FotoIDImage({ src, alt, label }) {
  const [error, setError] = useState(false);
  const [loaded, setLoaded] = useState(false);

  if (!src || error) {
    return (
      <div style={{ width: '100%', aspectRatio: '3/4', borderRadius: 'var(--radius-md)', backgroundColor: 'var(--gray-200)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--gray-500)' }}>
        <i className="bi bi-image" style={{ fontSize: '2rem' }}></i>
        <span style={{ fontSize: 'var(--font-size-xs)', marginTop: '4px' }}>{label || 'Sin foto'}</span>
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', width: '100%', aspectRatio: '3/4' }}>
      {!loaded && (
        <div style={{ position: 'absolute', inset: 0, borderRadius: 'var(--radius-md)', backgroundColor: 'var(--gray-200)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div className="spinner-border spinner-border-sm" style={{ color: 'var(--gray-400)' }}></div>
        </div>
      )}
      <img
        src={src} alt={alt}
        style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'var(--radius-md)', opacity: loaded ? 1 : 0, transition: 'opacity 0.3s ease' }}
        onLoad={() => setLoaded(true)}
        onError={() => setError(true)}
      />
    </div>
  );
}

// ─── Styles ────────────────────────────────────────────────

const getOverlayStyle = (isMobile) => ({
  position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.6)',
  display: 'flex',
  alignItems: isMobile ? 'stretch' : 'center',
  justifyContent: 'center',
  zIndex: 999999,
  backdropFilter: 'blur(2px)',
  padding: isMobile ? 0 : 'var(--space-3)'
});

const getModalContainerStyle = (isMobile) => ({
  backgroundColor: 'var(--white)',
  borderRadius: isMobile ? 0 : 'var(--radius-xl)',
  width: '100%',
  maxWidth: isMobile ? '100%' : '500px',
  maxHeight: isMobile ? '100%' : '90vh',
  height: isMobile ? '100%' : 'auto',
  display: 'flex', flexDirection: 'column', overflow: 'hidden',
  boxShadow: isMobile ? 'none' : 'var(--shadow-xl)',
  animation: isMobile ? 'none' : 'slideUp 0.3s ease-out'
});

const getHeaderStyle = (isMobile) => ({
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: 'var(--space-4)',
  background: 'linear-gradient(135deg, var(--feature-primary) 0%, var(--feature-secondary) 100%)',
  borderRadius: isMobile ? 0 : 'var(--radius-xl) var(--radius-xl) 0 0'
});

const footerStyle = {
  display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)',
  padding: 'var(--space-3) var(--space-4)',
  borderTop: '1px solid var(--gray-200)', backgroundColor: 'var(--gray-50)'
};

const featureRowStyle = {
  display: 'flex', alignItems: 'center', gap: '10px',
  padding: '8px 12px', backgroundColor: 'var(--gray-50)', borderRadius: 'var(--radius-md)'
};

const liStyle = {
  fontSize: 'var(--font-size-sm)', color: 'var(--gray-700)', marginBottom: '8px', lineHeight: '1.5'
};

const uploadAreaStyle = (hasPreview) => ({
  width: '100%', aspectRatio: '3/4', borderRadius: 'var(--radius-md)',
  border: hasPreview ? 'none' : '2px dashed var(--gray-300)',
  backgroundColor: hasPreview ? 'transparent' : 'var(--gray-50)',
  cursor: 'pointer', overflow: 'hidden', transition: 'var(--transition-base)'
});

const placeholderStyle = {
  width: '100%', height: '100%', display: 'flex', flexDirection: 'column',
  alignItems: 'center', justifyContent: 'center'
};

const dashboardFieldStyle = {
  marginBottom: '16px'
};

const dashboardLabelStyle = {
  display: 'flex', alignItems: 'center', gap: '8px',
  fontWeight: 'var(--font-weight-semibold)', fontSize: 'var(--font-size-sm)',
  color: 'var(--gray-800)', marginBottom: '0'
};

const dashboardValueStyle = {
  marginTop: '8px', padding: '10px 14px',
  backgroundColor: 'var(--gray-50)', borderRadius: 'var(--radius-md)',
  fontSize: 'var(--font-size-sm)', color: 'var(--gray-800)'
};

const editSelectStyle = {
  marginTop: '8px', width: '100%', padding: '10px 14px',
  borderRadius: 'var(--radius-md)', border: '1px solid var(--gray-300)',
  fontSize: 'var(--font-size-sm)', color: 'var(--gray-800)',
  backgroundColor: 'var(--white)', appearance: 'auto'
};

const editTextareaStyle = {
  marginTop: '8px', width: '100%', padding: '10px 14px',
  borderRadius: 'var(--radius-md)', border: '1px solid var(--gray-300)',
  fontSize: 'var(--font-size-sm)', color: 'var(--gray-800)',
  backgroundColor: 'var(--white)', resize: 'vertical', fontFamily: 'inherit'
};
